import { IData } from "../../../shared/interfaces/IData";
import { IContribution } from "../../../shared/interfaces/IContribution";

export default class SConsciousApiService {

    public getUser = async (userId: string, userEmail?: string) => {
        try {
            const params = new URLSearchParams({ userId });
            if (userEmail) params.set('userEmail', userEmail);
            const response = await fetch(`/api/user?${params}`, { method: 'GET' });
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error('Error fetching user:', error);
            return null;
        }
    };

    public listQueue = async (userId: string) => {
        try {
            let result = await fetch(`/api/listQueue?userId=${userId}`, {
                method: 'GET',
            });
            const data = await result.json();
            return data;
        
        } catch (error) {
            console.error("Error listing processing queue:", error);
            return error;
        }
    };

    /** Returns the URL for retrieving a blob image via the server proxy.
     *  No network call needed — the returned URL is used directly as <img src>. */
    public getImageUrl = (userId: string, blobName: string): string => {
        return `/api/getImage?userId=${encodeURIComponent(userId)}&blobName=${encodeURIComponent(blobName)}`;
    };
    public deleteReceiptBlob = async (userId: string, blobName: string): Promise<boolean> => {
        try {
            const response = await fetch('/api/receipt/blob', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, blobName }),
            });
            return response.ok;
        } catch { return false; }
    };
    public getAppData = async (userId: string): Promise<IData> => {

        try {
            const response = await fetch(`/api/getAppData?userId=${userId}`, {
                method: 'GET'
            });
            const data = await response.json();
            if (response.ok) {
                localStorage.setItem('appDataCache', JSON.stringify(data));
            } else {
                // Non-OK (e.g. 503 offline fallback from SW) — serve cache if available
                const cached = localStorage.getItem('appDataCache');
                if (cached) return JSON.parse(cached);
            }
            return data;
        } catch (error) {
            console.error("Error getting reports data:", error);
            try {
                const cached = localStorage.getItem('appDataCache');
                if (cached) return JSON.parse(cached);
            } catch { /* ignore */ }
            return {} as IData;
        }
    };

    public deleteItem = async (itemId: string, receiptId: string, userId: string): Promise<any> => {
        try {
            const response = await fetch('/api/item', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ itemId, receiptId, userId }),
            });
            return await response.json();
        } catch (error) {
            console.error('Error deleting item:', error);
            return { error: String(error) };
        }
    };

    public reanalyzeReceipt = async (receiptId: string, userId: string, userRemark?: string): Promise<any> => {
        try {
            const response = await fetch('/api/receipt/reanalyze', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ receiptId, userId, userRemark }),
            });
            return await response.json();
        } catch (error) {
            console.error('Error re-analyzing receipt:', error);
            return { error: String(error) };
        }
    };

    public rescanReceipt = async (receiptId: string, userId: string): Promise<any> => {
        try {
            const response = await fetch('/api/receipt/rescan', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ receiptId, userId }),
            });
            return await response.json();
        } catch (error) {
            return { error: String(error) };
        }
    };

    public deleteReceiptData = async (receiptId: string, userId: string, blobName: string) => {

        try {
            const response = await fetch(`/api/deleteReceiptData?receiptId=${receiptId}&userId=${userId}&blobName=${blobName}`, {
            method: 'DELETE'
            });
            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error getting reports data:", error);
            return error;
        }
    };

    public async getFriends(userId: string): Promise<any[]> {
        try {
            const response = await fetch(`/api/friends?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return [];
            return await response.json();
        } catch { return []; }
    }

    public async getMerchantSocial(userId: string, merchantId: string): Promise<{ friends: any[], strangers: number, likedFriendIds: string[] }> {
        try {
            const response = await fetch(`/api/merchant/social?userId=${encodeURIComponent(userId)}&merchantId=${encodeURIComponent(merchantId)}`);
            if (!response.ok) return { friends: [], strangers: 0, likedFriendIds: [] };
            return await response.json();
        } catch { return { friends: [], strangers: 0, likedFriendIds: [] }; }
    }

    public async sendLike(userId: string, toUserId: string, merchantName: string, merchantId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/merchant/like', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, toUserId, merchantName, merchantId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async updateMerchantAddress(merchantId: string, address: { street_address: string; city: string; postal_code: string; country: string }): Promise<{ success: boolean; merchant?: any; error?: string }> {
        try {
            const response = await fetch('/api/merchant/address', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ merchantId, ...address }),
            });
            return await response.json();
        } catch { return { success: false, error: 'Network error' }; }
    }

    public async getChatAdmins(): Promise<string[]> {
        try {
            const response = await fetch('/api/chat/admins');
            if (!response.ok) return [];
            return await response.json();
        } catch { return []; }
    }

    public async getChatConversations(userId: string): Promise<any[]> {
        try {
            const response = await fetch(`/api/chat/conversations?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return [];
            return await response.json();
        } catch { return []; }
    }

    public async markConversationRead(userId: string, conversationId: string): Promise<void> {
        try {
            await fetch('/api/chat/conversation/read', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, conversationId }),
            });
        } catch { /* fire and forget */ }
    }

    public async getChatMessages(conversationId: string, userId: string): Promise<any[]> {
        try {
            const response = await fetch(`/api/chat/messages?conversationId=${encodeURIComponent(conversationId)}&userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return [];
            return await response.json();
        } catch { return []; }
    }

    public async sendChatMessage(fromUserId: string, toUserId: string, message: string, type: 'chat' | 'feedback', attachmentUrl?: string, attachmentName?: string): Promise<boolean> {
        try {
            const response = await fetch('/api/chat/message', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ fromUserId, toUserId, message, type, attachmentUrl, attachmentName }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async deleteChatConversation(conversationId: string, userId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/chat/conversation', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ conversationId, userId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async deleteChatMessage(messageId: string, userId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/chat/message', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ messageId, userId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async editChatMessage(messageId: string, userId: string, message: string): Promise<boolean> {
        try {
            const response = await fetch('/api/chat/message', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ messageId, userId, message }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async uploadChatAttachment(userId: string, imgSrc: string, fileName: string): Promise<{ url: string; blobName: string } | null> {
        try {
            const response = await fetch('/api/chat/upload', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, imgSrc, fileName }),
            });
            if (!response.ok) return null;
            return await response.json();
        } catch { return null; }
    }

    public async uploadProfilePhoto(userId: string, imgSrc: string): Promise<{ url: string } | null> {
        try {
            const response = await fetch('/api/profile/upload', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, imgSrc }),
            });
            if (!response.ok) return null;
            return await response.json();
        } catch { return null; }
    }

    public async deleteProfilePhoto(userId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/profile/photo', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async uploadFidelityCard(userId: string, imgSrc: string, cardName: string): Promise<{ url: string; blobName: string; name: string } | null> {
        try {
            const response = await fetch('/api/fidelity/upload', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, imgSrc, cardName }),
            });
            if (!response.ok) return null;
            return await response.json();
        } catch { return null; }
    }

    public async listFidelityCards(userId: string): Promise<{ blobName: string; url: string; name: string; createdOn: string }[]> {
        try {
            const response = await fetch(`/api/fidelity/cards?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return [];
            return await response.json();
        } catch { return []; }
    }

    public async deleteFidelityCard(userId: string, blobName: string): Promise<boolean> {
        try {
            const response = await fetch('/api/fidelity/card', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, blobName }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async searchUsers(q: string, userId: string): Promise<{ user_id: string; user_name: string; user_email: string }[]> {
        try {
            const response = await fetch(`/api/users/search?q=${encodeURIComponent(q)}&userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return [];
            return await response.json();
        } catch { return []; }
    }

    public async getUserProfile(userId: string, targetUserId: string): Promise<any | null> {
        try {
            const params = new URLSearchParams({ userId, targetUserId });
            const response = await fetch(`/api/users/profile?${params}`);
            if (!response.ok) return null;
            return await response.json();
        } catch { return null; }
    }

    public async blockUser(userId: string, targetUserId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/users/block', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, targetUserId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async unblockUser(userId: string, targetUserId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/users/block', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, targetUserId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async inviteFriend(userId: string, friendEmail: string): Promise<boolean> {
        try {
            const response = await fetch('/api/friends/invite', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, friendEmail }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async removeFriend(userId: string, inviteId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/friends', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, inviteId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async cancelInvite(userId: string, inviteId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/friends/cancel', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, inviteId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async sendFriendRequest(userId: string, toUserId: string): Promise<{ success: boolean; alreadyExists?: boolean }> {
        try {
            const response = await fetch('/api/friends/request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, toUserId }),
            });
            if (!response.ok) return { success: false };
            return await response.json();
        } catch { return { success: false }; }
    }

    public async acceptFriendRequest(userId: string, inviteId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/friends/accept', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, inviteId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async searchAI(query: string, userId: string, indexName?: string, top = 200, semantic = false): Promise<{ count: number; results: any[] }> {
        try {
            const params = new URLSearchParams({ q: query, userId, top: String(top) });
            if (indexName) params.set('index', indexName);
            if (semantic) params.set('semantic', 'true');
            const response = await fetch(`/api/search?${params.toString()}`);
            if (!response.ok) return { count: 0, results: [] };
            return await response.json();
        } catch { return { count: 0, results: [] }; }
    }

    public async parseSearchQuery(query: string, userId: string): Promise<{
        count: number;
        results: any[];
        parsed: { query: string; index: string; filters: string; semantic: boolean; explanation: string };
    }> {
        try {
            const params = new URLSearchParams({ q: query, userId });
            const response = await fetch(`/api/search/parse?${params.toString()}`);
            if (!response.ok) return { count: 0, results: [], parsed: { query, index: 'product-items', filters: '', semantic: false, explanation: '' } };
            return await response.json();
        } catch { return { count: 0, results: [], parsed: { query, index: 'product-items', filters: '', semantic: false, explanation: '' } }; }
    }

    public async suggestAI(query: string, indexName: string, suggesterName: string): Promise<any[]> {
        try {
            const params = new URLSearchParams({ q: query, index: indexName, suggester: suggesterName });
            const response = await fetch(`/api/search/suggest?${params.toString()}`);
            if (!response.ok) return [];
            return await response.json();
        } catch { return []; }
    }

    public async logEvent(event: string, properties: Record<string, any>): Promise<void> {
        try {
            //console.log(event, properties);
            await fetch(`/api/log`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ event, properties })
            });
        } catch (error) {
            console.error("Error logging event:", error);
        }
    }

    public async suggestItemBrands(description: string, merchantName: string, merchantCity: string | undefined, userId: string, productIdentifier?: string, productType?: string): Promise<{ brand: string; corporation: string }[]> {
        try {
            const response = await fetch('/api/item/suggest-brands', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ description, merchantName, merchantCity, userId, productIdentifier, productType }),
            });
            const data = await response.json();
            return Array.isArray(data.suggestions) ? data.suggestions : [];
        } catch (error) {
            return [];
        }
    }

    public async getCanonicalProduct(productIdentifier: string): Promise<any | null> {
        try {
            const response = await fetch(`/api/canonical-product?productIdentifier=${encodeURIComponent(productIdentifier)}`);
            if (!response.ok) return null;
            return response.json();
        } catch {
            return null;
        }
    }

    public async updateItemBrand(itemId: string, receiptId: string, brand: string, owningCorporation: string, userId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/item/brand', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ item_id: itemId, receipt_id: receiptId, brand, owning_corporation: owningCorporation, userId }),
            });
            return response.ok;
        } catch (error) {
            return false;
        }
    }

    public async suggestCorporations(brandName: string, userId: string, existingCorporation?: string): Promise<string[]> {
        try {
            const response = await fetch('/api/brand/suggest-corporations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ brandName, userId, existingCorporation }),
            });
            const data = await response.json();
            return Array.isArray(data.suggestions) ? data.suggestions : [];
        } catch {
            return [];
        }
    }

    public async updateBrandCorporation(brandName: string, corporation: string, userId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/brand/corporation', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ brandName, corporation, userId }),
            });
            return response.ok;
        } catch {
            return false;
        }
    }

    public async getReceiptMerchantBranches(receiptId: string, userId: string): Promise<{ suggestions: { merchant_id: string; name: string; city?: string; street_address?: string; postal_code?: string; country?: string; owning_corporation?: string; confidence: string; _isNew?: boolean }[]; owning_corporation: string | null }> {
        try {
            const response = await fetch('/api/receipt/merchant-branches', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ receiptId, userId }),
            });
            const data = await response.json();
            return { suggestions: Array.isArray(data.suggestions) ? data.suggestions : [], owning_corporation: data.owning_corporation || null };
        } catch {
            return { suggestions: [], owning_corporation: null };
        }
    }

    public async suggestReceiptMerchants(receiptId: string, userId: string): Promise<{ merchant_id: string; name: string; city?: string; street_address?: string; postal_code?: string; country?: string; owning_corporation?: string; confidence: string; _isNew?: boolean }[]> {
        try {
            const response = await fetch('/api/receipt/suggest-merchants', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ receiptId, userId }),
            });
            const data = await response.json();
            return Array.isArray(data.suggestions) ? data.suggestions : [];
        } catch {
            return [];
        }
    }

    public async updateReceiptMerchant(receiptId: string, merchantId: string, userId: string, newMerchantData?: { name: string; city?: string; street_address?: string; postal_code?: string; country?: string; owning_corporation?: string }): Promise<{ success: boolean; merchant?: any }> {
        try {
            const response = await fetch('/api/receipt/merchant', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ receiptId, merchantId, userId, newMerchantData }),
            });
            if (!response.ok) return { success: false };
            return response.json();
        } catch {
            return { success: false };
        }
    }

    public async getContributions(userId: string): Promise<{ contributions: IContribution[]; totalPoints: number }> {
        try {
            const response = await fetch(`/api/contributions?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return { contributions: [], totalPoints: 0 };
            return response.json();
        } catch {
            return { contributions: [], totalPoints: 0 };
        }
    }

    public async scanProductPhoto(userId: string, imgSrc: string, itemId?: string, description?: string): Promise<{ url: string; suggestions: { brand: string; corporation: string }[]; rewarded?: boolean; relevant?: boolean } | null | 'rate_limited'> {
        try {
            const response = await fetch('/api/product/scan', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, imgSrc, itemId, description }),
            });
            if (response.status === 429) return 'rate_limited';
            if (!response.ok) return null;
            return response.json();
        } catch {
            return null;
        }
    }

    public async claimBrandScanConsolation(userId: string, itemId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/product/scan/consolation', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, itemId }),
            });
            if (!response.ok) return false;
            const data = await response.json();
            return data.rewarded ?? false;
        } catch {
            return false;
        }
    }

    // ── Bill Split ──────────────────────────────────────────────────────────

    public async createBillSplit(
        userId: string,
        receiptId: string,
        mode: 'items' | 'equal',
        totalAmount: number,
        currencyCode: string,
        participants: { user_id?: string; email: string; name: string; item_ids?: string[]; amount: number }[]
    ): Promise<any> {
        try {
            const response = await fetch('/api/billsplit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, receiptId, mode, totalAmount, currencyCode, participants }),
            });
            if (!response.ok) return null;
            return await response.json();
        } catch { return null; }
    }

    public async getBillSplit(receiptId: string): Promise<any> {
        try {
            const response = await fetch(`/api/billsplit?receiptId=${encodeURIComponent(receiptId)}`);
            if (!response.ok) return null;
            return await response.json();
        } catch { return null; }
    }

    public async claimBillSplitItems(splitId: string, participantId: string, itemClaims: { id: string; qty: number }[], amount: number, userId?: string): Promise<boolean> {
        try {
            const response = await fetch('/api/billsplit/claim', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ splitId, participantId, itemClaims, amount, userId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async getBillSplitById(splitId: string): Promise<any> {
        try {
            const response = await fetch(`/api/billsplit/id?splitId=${encodeURIComponent(splitId)}`);
            if (!response.ok) return null;
            return await response.json();
        } catch { return null; }
    }

    public async getBillSplitLive(splitId: string): Promise<any> {
        try {
            const response = await fetch(`/api/billsplit/live?splitId=${encodeURIComponent(splitId)}`);
            if (!response.ok) return null;
            return await response.json();
        } catch { return null; }
    }

    public async getBillSplitChat(splitId: string): Promise<any[]> {
        try {
            const response = await fetch(`/api/billsplit/chat?splitId=${encodeURIComponent(splitId)}`);
            if (!response.ok) return [];
            return await response.json();
        } catch { return []; }
    }

    public async sendBillSplitChat(userId: string, splitId: string, messageText: string): Promise<boolean> {
        try {
            const response = await fetch('/api/billsplit/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, splitId, messageText }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async settleBillSplit(receiptId: string, splitId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/billsplit/settle', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ receiptId, splitId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async deleteBillSplit(receiptId: string, splitId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/billsplit', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ receiptId, splitId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async acceptBillSplit(userId: string, splitId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/billsplit/accept', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, splitId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async confirmBillSplit(userId: string, splitId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/billsplit/confirm', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, splitId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async remindBillSplit(userId: string, splitId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/billsplit/remind', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, splitId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async unconfirmBillSplit(userId: string, splitId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/billsplit/unconfirm', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, splitId }),
            });
            return response.ok;
        } catch { return false; }
    }

    // ── Admin Portal ────────────────────────────────────────────────────────

    public async adminCheck(userId: string): Promise<boolean> {
        try {
            const response = await fetch(`/api/admin/check?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return false;
            const data = await response.json();
            return data.admin === true;
        } catch { return false; }
    }

    public async adminGetStats(userId: string): Promise<Record<string, number>> {
        try {
            const response = await fetch(`/api/admin/stats?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return {};
            return await response.json();
        } catch { return {}; }
    }

    public async adminGetTables(userId: string): Promise<{ name: string }[]> {
        try {
            const response = await fetch(`/api/admin/tables?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return [];
            return await response.json();
        } catch { return []; }
    }

    public async adminGetTableData(userId: string, tableName: string, top = 50, skip = 0, filter?: string): Promise<{ total: number; top: number; skip: number; data: any[] }> {
        try {
            const params = new URLSearchParams({ userId, top: String(top), skip: String(skip) });
            if (filter) params.set('filter', filter);
            const response = await fetch(`/api/admin/tables/${encodeURIComponent(tableName)}?${params}`);
            if (!response.ok) return { total: 0, top, skip, data: [] };
            return await response.json();
        } catch { return { total: 0, top, skip, data: [] }; }
    }

    public async adminGetEntity(userId: string, table: string, partitionKey: string, rowKey: string): Promise<any | null> {
        try {
            const params = new URLSearchParams({ userId, table, partitionKey, rowKey });
            const response = await fetch(`/api/admin/entity?${params}`);
            if (!response.ok) return null;
            return await response.json();
        } catch { return null; }
    }

    public async adminUpdateEntity(userId: string, table: string, partitionKey: string, rowKey: string, data: Record<string, any>): Promise<boolean> {
        try {
            const response = await fetch('/api/admin/entity', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, table, partitionKey, rowKey, data }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async adminDeleteEntity(userId: string, table: string, partitionKey: string, rowKey: string): Promise<boolean> {
        try {
            const response = await fetch('/api/admin/entity', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, table, partitionKey, rowKey }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async adminGetUsers(userId: string): Promise<any[]> {
        try {
            const response = await fetch(`/api/admin/users?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return [];
            return await response.json();
        } catch { return []; }
    }

    public async adminAddAdmin(userId: string, targetUserId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/admin/admins', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, targetUserId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async adminRemoveAdmin(userId: string, targetUserId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/admin/admins', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, targetUserId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async adminGetUserReceipts(userId: string, targetUserId: string): Promise<any[]> {
        try {
            const response = await fetch(`/api/admin/user/${encodeURIComponent(targetUserId)}/receipts?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return [];
            return await response.json();
        } catch { return []; }
    }

    public async adminGetUserItems(userId: string, targetUserId: string): Promise<any[]> {
        try {
            const response = await fetch(`/api/admin/user/${encodeURIComponent(targetUserId)}/items?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return [];
            return await response.json();
        } catch { return []; }
    }

    public async adminGetAnalytics(userId: string): Promise<any> {
        try {
            const response = await fetch(`/api/admin/analytics?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return null;
            return await response.json();
        } catch { return null; }
    }

    public async adminGetTokenHealth(userId: string): Promise<{ count: number; avg: number; p50: number; p90: number; p95: number; max: number; over20k: number; over30k: number; recent30Avg: number } | null> {
        try {
            const response = await fetch(`/api/admin/token-health?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return null;
            return await response.json();
        } catch { return null; }
    }

    public async adminGetDataQuality(userId: string): Promise<any> {
        try {
            const response = await fetch(`/api/admin/data-quality?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return null;
            return await response.json();
        } catch { return null; }
    }

    public async adminGetUnidentifiedBrands(userId: string): Promise<any> {
        try {
            const response = await fetch(`/api/admin/unidentified-brands?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return null;
            return await response.json();
        } catch { return null; }
    }

    public async adminSendNotification(userId: string, targetUserId: string | null, title: string, body: string, broadcast: boolean): Promise<any> {
        try {
            const response = await fetch('/api/admin/notify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, targetUserId, title, body, broadcast }),
            });
            if (!response.ok) return null;
            return await response.json();
        } catch { return null; }
    }

    public async adminGetActivity(userId: string, limit = 50): Promise<any[]> {
        try {
            const response = await fetch(`/api/admin/activity?userId=${encodeURIComponent(userId)}&limit=${limit}`);
            if (!response.ok) return [];
            return await response.json();
        } catch { return []; }
    }

    public async adminSearch(userId: string, query: string): Promise<{ query: string; totalFound: number; results: { table: string; matches: any[] }[] }> {
        try {
            const response = await fetch(`/api/admin/search?userId=${encodeURIComponent(userId)}&q=${encodeURIComponent(query)}`);
            if (!response.ok) return { query, totalFound: 0, results: [] };
            return await response.json();
        } catch { return { query, totalFound: 0, results: [] }; }
    }

    public async adminGetPromptSuggestions(userId: string, top = 100, skip = 0, targetUserId?: string): Promise<{ total: number; top: number; skip: number; data: any[] }> {
        try {
            const params = new URLSearchParams({ userId, top: String(top), skip: String(skip) });
            if (targetUserId) params.set('targetUserId', targetUserId);
            const response = await fetch(`/api/admin/prompt-suggestions?${params.toString()}`);
            if (!response.ok) return { total: 0, top, skip, data: [] };
            return await response.json();
        } catch {
            return { total: 0, top, skip, data: [] };
        }
    }

    public async adminGetPromptGuidance(userId: string, includeInactive = true): Promise<any[]> {
        try {
            const response = await fetch(`/api/admin/prompt-guidance?userId=${encodeURIComponent(userId)}&includeInactive=${includeInactive ? '1' : '0'}`);
            if (!response.ok) return [];
            return await response.json();
        } catch {
            return [];
        }
    }

    public async adminAddPromptGuidance(userId: string, guidanceText: string, priority: number = 100, force: boolean = false): Promise<any | null> {
        try {
            const response = await fetch('/api/admin/prompt-guidance', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, guidanceText, priority, force }),
            });
            if (!response.ok) {
                const body = await response.json().catch(() => null);
                if (response.status === 409) {
                    return { conflict: true, conflicts: body?.conflicts || [] };
                }
                return null;
            }
            return await response.json();
        } catch {
            return null;
        }
    }

    public async createManualReceipt(
        userId: string,
        merchantName: string,
        amount: number,
        currencyCode: string,
        transactionDate: string,
        category: string,
        recurring?: string,
        description?: string,
    ): Promise<any> {
        try {
            const response = await fetch('/api/receipt/manual', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, merchantName, amount, currencyCode, transactionDate, category, recurring, description }),
            });
            return await response.json();
        } catch {
            return { error: 'Network error' };
        }
    }

    public async cancelRecurring(receiptId: string, userId: string): Promise<boolean> {
        try {
            const response = await fetch('/api/receipt/cancel-recurring', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ receiptId, userId }),
            });
            return response.ok;
        } catch { return false; }
    }

    public async adminValidatePromptGuidance(userId: string, guidanceText: string): Promise<{ hasConflicts: boolean; conflicts: any[] } | null> {
        try {
            const response = await fetch('/api/admin/prompt-guidance/validate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, guidanceText }),
            });
            if (!response.ok) return null;
            return await response.json();
        } catch {
            return null;
        }
    }

    public async adminSetPromptGuidanceActive(userId: string, promptGuidanceId: string, active: boolean): Promise<boolean> {
        try {
            const response = await fetch(`/api/admin/prompt-guidance/${encodeURIComponent(promptGuidanceId)}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, active }),
            });
            return response.ok;
        } catch {
            return false;
        }
    }

    public async adminSetPromptGuidancePriority(userId: string, promptGuidanceId: string, priority: number): Promise<boolean> {
        try {
            const response = await fetch(`/api/admin/prompt-guidance/${encodeURIComponent(promptGuidanceId)}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, priority }),
            });
            return response.ok;
        } catch {
            return false;
        }
    }

    public async adminDeletePromptGuidance(userId: string, promptGuidanceId: string): Promise<boolean> {
        try {
            const response = await fetch(`/api/admin/prompt-guidance/${encodeURIComponent(promptGuidanceId)}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId }),
            });
            return response.ok;
        } catch {
            return false;
        }
    }

    public async adminGetPromptGuidancePreview(userId: string): Promise<{ active_count: number; active: any[]; preview_block: string } | null> {
        try {
            const response = await fetch(`/api/admin/prompt-guidance/preview?userId=${encodeURIComponent(userId)}`);
            if (!response.ok) return null;
            return await response.json();
        } catch {
            return null;
        }
    }

    public async adminGetLogs(userId: string, options?: { date?: string; level?: string; search?: string; top?: number; skip?: number }): Promise<{ total: number; data: any[] }> {
        try {
            const params = new URLSearchParams({ userId });
            if (options?.date) params.set('date', options.date);
            if (options?.level) params.set('level', options.level);
            if (options?.search) params.set('search', options.search);
            if (options?.top) params.set('top', String(options.top));
            if (options?.skip) params.set('skip', String(options.skip));
            const response = await fetch(`/api/admin/logs?${params.toString()}`);
            if (!response.ok) return { total: 0, data: [] };
            return await response.json();
        } catch {
            return { total: 0, data: [] };
        }
    }
}